import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';

import type { TerminalHoldErrorSpec } from '../bookingRecovery';
import { formatHoldExpiry } from '../format';
import { isReadableExpiry } from '../holdCountdown';

interface Props {
  spec: TerminalHoldErrorSpec;
  /** El motivo TAL CUAL lo dio el operador: es lo único accionable que hay. */
  message?: string;
  /** Rail: instante absoluto del hold temporal ya creado por el proveedor. */
  providerHoldExpiresAt?: string;
  onSearchAgain: () => void;
  /**
   * Re-cotiza el MISMO pedido en el momento (búsqueda idéntica, generación
   * nueva) sin cerrar el modal. Solo se dibuja cuando la spec lo declara
   * (`offersRequote`) Y el buscador sabe re-cotizar — un botón que promete lo
   * que el vertical no ofrece sería un botón muerto.
   */
  onRequote?: () => void;
  requoting?: boolean;
}

/**
 * Una respuesta de hold que necesita una salida explícita del asesor. La
 * mayoría son rechazos limpios; Rail también usa esta cara mientras el hold
 * remoto temporal sigue vivo y Bandido bloquea el reintento hasta su expiry.
 *
 * Es el único rojo de esta pantalla, y existe justamente para que los estados
 * recuperables no se pinten así. El texto del proveedor va TEXTUAL: es lo que
 * le dice al asesor qué pasó y qué explicarle al pasajero; reemplazarlo por un
 * mensaje fijo obliga a reproducir el hold a mano para saberlo. Las specs con
 * `bodyKey` son la excepción: ahí manda siempre el texto traducido.
 */
export function HoldTerminalErrorCard({
  spec,
  message,
  providerHoldExpiresAt,
  onSearchAgain,
  onRequote,
  requoting,
}: Props) {
  const { t } = useTranslation();
  const showRequote = Boolean(spec.offersRequote && onRequote);
  const showProviderHoldExpiry = isReadableExpiry(providerHoldExpiresAt);

  return (
    <Alert severity="error" icon={false}>
      <AlertTitle>{t(spec.titleKey)}</AlertTitle>
      <Typography variant="body2">
        {spec.bodyKey
          ? t(spec.bodyKey)
          : message || t('travelServices.booking.recovery.terminal.noProviderMessage')}
      </Typography>
      {/* Cuando el rechazo NO mata la oferta, el camino corto es corregir lo
          que el proveedor nombró y volver a reservar: la salida de arriba no
          puede ser la única que se ofrece. */}
      {spec.hintKey && (
        <Typography variant="body2" mt={1}>
          {t(spec.hintKey)}
        </Typography>
      )}
      {showProviderHoldExpiry && (
        <Typography variant="body2" mt={1} fontWeight={600}>
          {t('travelServices.booking.recovery.terminal.providerTemporaryHoldExpiresAt', {
            date: formatHoldExpiry(providerHoldExpiresAt as string),
          })}
        </Typography>
      )}
      <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mt={1.5}>
        {/* La oferta venció pero el pedido sigue siendo válido: re-cotizar lo
            mismo produce una oferta vigente sin perder lo cargado. Es la
            acción primaria de ESTA tarjeta, por delante de cerrar y empezar
            de cero. */}
        {showRequote && (
          <LoadingButton
            size="small"
            color="error"
            variant="contained"
            loading={requoting}
            onClick={onRequote}
          >
            {t('travelServices.booking.recovery.terminal.requote')}
          </LoadingButton>
        )}
        {/* Sale del modal: la búsqueda queda atrás. Va outlined a propósito —
            cuando el proveedor nombró un dato corregible, lo primero es
            corregirlo, y un botón macizo empujaría a cerrar y perder lo
            cargado. */}
        <Button size="small" color="error" variant="outlined" onClick={onSearchAgain}>
          {t('travelServices.booking.recovery.terminal.searchAgain')}
        </Button>
        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
          {spec.code}
        </Typography>
      </Box>
    </Alert>
  );
}
